import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Check, Search, Package } from 'lucide-react';
import { ProductSearchSelect } from '../components/ProductSearchSelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function BuyStockPage() {
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form State
    const [selectedProduct, setSelectedProduct] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [buyPrice, setBuyPrice] = useState('');
    const [supplier, setSupplier] = useState('');
    const [notes, setNotes] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);

    // Confirm Dialog State
    const [confirmOpen, setConfirmOpen] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get(`${API_URL}/products`, { headers });
                setProducts(res.data.data.products);
            } catch (error) {
                console.error('Failed to fetch products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const isFormValid = selectedProduct && parseInt(quantity) > 0 && parseFloat(buyPrice) >= 0;

    const handleConfirm = () => {
        if (!isFormValid) return;
        setConfirmOpen(true);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await axios.post(`${API_URL}/inventory/buy-stock`, {
                productId: selectedProduct,
                quantity: parseInt(quantity),
                buyPrice: parseFloat(buyPrice),
                supplier,
                notes,
                date: new Date(date).toISOString()
            }, { headers });

            // Reset form on success
            setSelectedProduct('');
            setQuantity('1');
            setBuyPrice('');
            setSupplier('');
            setNotes('');
            setDate(new Date().toISOString().split('T')[0]);
            setConfirmOpen(false);
            toast.success(t('successCreated') || 'Successfully bought stock!');

        } catch (error) {
            console.error('Failed to buy stock:', error);
            toast.error(error.response?.data?.error || t('errorOccurred'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedProductObj = products.find(p => p.id === selectedProduct);
    const totalAmount = (parseFloat(buyPrice || 0) * parseInt(quantity || 0)).toFixed(2);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 max-w-2xl mx-auto">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Plus className="h-6 w-6" />
                        {t('buyStock')}
                    </h1>
                    <p className="text-muted-foreground">{t('buyStockDesc')}</p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('buyStock')}</CardTitle>
                        <CardDescription>{t('fillRequired')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <ProductSearchSelect
                                products={products}
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                                placeholder={t('selectProduct')}
                                label={t('product')}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t('quantity')}</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('buyPrice')} (TND)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={buyPrice}
                                    onChange={(e) => setBuyPrice(e.target.value)}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t('date') || 'Date'}</Label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('supplier')} ({t('notesOptional')})</Label>
                                <Input
                                    value={supplier}
                                    onChange={(e) => setSupplier(e.target.value)}
                                    placeholder={t('supplier')}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>{t('notes')} ({t('notesOptional')})</Label>
                            <Input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('notesPlaceholder')}
                            />
                        </div>

                        <div className="pt-4 flex items-center justify-between border-t">
                            <div className="text-lg font-bold">
                                {t('total')}: {totalAmount} TND
                            </div>
                            <Button onClick={handleConfirm} disabled={!isFormValid || isSubmitting}>
                                <Check className="h-4 w-4 mr-2" />
                                {t('confirm')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Confirmation Dialog */}
                <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{t('confirmBuyStock')}</DialogTitle>
                            <DialogDescription>
                                {t('confirmBuyStockMsg')
                                    ?.replace('{quantity}', quantity)
                                    ?.replace('{productName}', selectedProductObj?.name || 'product')
                                    ?.replace('{amount}', totalAmount)
                                }
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? t('loading') : t('confirm')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
